import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MagnifyingGlass, ArrowRight, SealCheck, Lightning, Clock, Briefcase, CalendarCheck, Sparkle, SlidersHorizontal } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";
import { ALL_CATEGORIES, CATEGORY_META } from "@/data/categories";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { ServiceCard } from "@/components/ServiceCard";
import { VerifiedSlider } from "@/components/VerifiedSlider";
import { LocationPicker, readCity } from "@/components/LocationPicker";
import { CategoryCard } from "@/components/CategoryCard";
import { Button } from "@/components/ui/button";

const EU_LABEL = {
  en: "Available in all Europe", nl: "Beschikbaar in heel Europa", pl: "Dostępne w całej Europie",
  it: "Disponibile in tutta Europa", sk: "Dostupné v celej Európe", es: "Disponible en toda Europa", pt: "Disponível em toda a Europa",
};
const SEE_ALL = { en: "See all", nl: "Bekijk alles", pl: "Zobacz wszystko", it: "Vedi tutti", sk: "Zobraziť všetko", es: "Ver todo", pt: "Ver tudo" };

const INTRO_VIDEO = {
  en: {
    src: "/media/plinplincare-intro.mp4",
    title: "See how PlinPlinCare works",
    sub: "A short look at booking trusted help near you.",
  },
  nl: {
    src: "/media/plinplincare-intro-nl.mp4",
    title: "Ontdek PlinPlinCare",
    sub: "Een korte kijk op het boeken van vertrouwde hulp bij jou in de buurt.",
  },
};


export default function Landing() {
  const { t, tc, lang } = useLang();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);
  const [counts, setCounts] = useState({});
  const [city, setCity] = useState(readCity());

  useEffect(() => {
    api
      .get("/listings")
      .then(({ data }) => {
        setFeatured(data.slice(0, 6));
        setCounts(data.reduce((acc, l) => ({ ...acc, [l.category]: (acc[l.category] || 0) + 1 }), {}));
      })
      .catch(() => setFeatured([]));
  }, []);

  const goSearch = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (cat) p.set("category", cat);
    if (city && city !== "all") p.set("city", city);
    navigate(`/browse?${p.toString()}`);
  };

  return (
    <div className="mx-auto max-w-7xl px-5 pb-8 lg:px-8">
      {/* Hero — mobile structure */}
      <section className="fade-up pt-6 lg:hidden">
        <div className="flex items-center justify-between gap-3">
          <LocationPicker value={city} onChange={setCity} />
          <Link
            to="/browse"
            data-testid="mobile-filters-link"
            className="glass-pill grid h-11 w-11 place-items-center !rounded-2xl !p-0"
            aria-label={t("nav_browse")}
          >
            <SlidersHorizontal size={18} color="#c6bff0" />
          </Link>
        </div>

        <form onSubmit={goSearch} className="mt-4 flex items-center gap-2.5">
          <label className="search-inset flex h-12 flex-1 items-center gap-2.5 rounded-2xl px-4">
            <MagnifyingGlass size={18} color="#9184d9" />
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("browse_search")}
              className="min-w-0 flex-1 bg-transparent text-[14px] text-ppc-text outline-none placeholder:text-ppc-muted"
            />
          </label>
          <button type="submit" data-testid="mobile-search-btn" className="btn-primary grid h-12 w-12 shrink-0 place-items-center rounded-2xl">
            <ArrowRight size={18} />
          </button>
        </form>

        <h1 className="mt-7 text-[32px] leading-[1.1] tracking-[-0.02em] text-ppc-text">{t("hero_title")}</h1>
        <p className="mt-3 text-sm text-ppc-muted">{t("hero_sub")}</p>

        <div className="no-scrollbar mt-5 -mx-5 flex gap-2 overflow-x-auto px-5 pb-1">
          <button
            type="button"
            data-testid="mobile-chip-all"
            onClick={() => setCat(null)}
            className={`chip ${!cat ? "chip-active" : ""}`}
          >
            {t("filter_all")}
          </button>
          {ALL_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`mobile-chip-${c}`}
              onClick={() => setCat(cat === c ? null : c)}
              className={`chip chip-img ${cat === c ? "chip-active" : ""}`}
            >
              <img src={CATEGORY_META[c].image} alt="" className="chip-thumb" loading="lazy" />
              {tc(c)}
            </button>
          ))}
        </div>

        {featured?.length > 0 && (
          <div className="mt-6" data-testid="mobile-featured-card">
            <VerifiedSlider
              endpoint="/listings"
              city={city}
              title={t("slider_featured")}
              subtitle={t("slider_swipe")}
              testId="home-slider"
            />
          </div>
        )}

        <div className="mt-4 grid grid-cols-3 gap-2.5">
          <div className="card-list p-3.5" data-testid="mobile-stat-eu">
            <SealCheck size={16} weight="fill" color="#9184d9" />
            <p className="mt-1.5 text-[11.5px] leading-tight text-ppc-text">{EU_LABEL[lang] || EU_LABEL.en}</p>
          </div>
          <div className="card-list p-3.5">
            <p className="text-xl text-ppc-text">40+</p>
            <p className="mt-1 text-[11px] text-ppc-muted">{t("hero_stat2")}</p>
          </div>
          <div className="card-list p-3.5">
            <p className="text-xl text-ppc-text">7</p>
            <p className="mt-1 text-[11px] text-ppc-muted">{t("hero_stat3")}</p>
          </div>
        </div>
      </section>

      {/* Hero — desktop (unchanged) */}
      <section className="fade-up hidden items-start gap-8 pt-10 lg:grid lg:grid-cols-[1.1fr_1fr] lg:gap-14 lg:pt-16">
        <div>
          <h1 className="text-4xl leading-[1.08] tracking-[-0.02em] text-ppc-text sm:text-5xl lg:text-6xl">
            {t("hero_title")}
          </h1>
          <p className="mt-4 max-w-lg text-base text-ppc-muted md:text-lg">{t("hero_sub")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" data-testid="hero-find-btn" className="btn-primary h-12 rounded-xl px-6">
              <Link to="/browse">{t("hero_cta")} <ArrowRight size={16} className="ml-1" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" data-testid="hero-offer-btn" className="btn-secondary h-12 rounded-xl px-6">
              <Link to="/create-listing">{t("hero_cta2")}</Link>
            </Button>
          </div>
          <div className="mt-10 grid max-w-lg grid-cols-3 gap-3">
            <div className="card-list p-4" data-testid="hero-stat-eu">
              <SealCheck size={18} weight="fill" color="#9184d9" />
              <p className="mt-2 text-[13px] leading-tight text-ppc-text">{EU_LABEL[lang] || EU_LABEL.en}</p>
            </div>
            <div className="card-list p-4">
              <p className="text-2xl text-ppc-text">40+</p>
              <p className="mt-1 text-[11.5px] text-ppc-muted">{t("hero_stat2")}</p>
            </div>
            <div className="card-list p-4">
              <p className="text-2xl text-ppc-text">7</p>
              <p className="mt-1 text-[11.5px] text-ppc-muted">{t("hero_stat3")}</p>
            </div>
          </div>
        </div>

        {/* Hero card: one big ask */}
        <form onSubmit={goSearch} className="card-hero p-6 lg:mt-6 lg:p-7" data-testid="hero-search-card">
          <h2 className="text-2xl leading-tight tracking-[-0.02em] text-ppc-text md:text-[26px]">{t("section_categories")}</h2>
          <p className="mt-1.5 text-[13px] text-ppc-muted">{t("section_categories_sub")}</p>
          <label className="search-inset mt-5 flex h-[52px] items-center gap-3 rounded-2xl px-4">
            <MagnifyingGlass size={19} color="#9184d9" />
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("browse_search")}
              className="flex-1 bg-transparent text-[15px] text-ppc-text outline-none placeholder:text-ppc-muted"
            />
          </label>
          <div className="mt-3.5 flex flex-wrap gap-2">
            {ALL_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`category-tile-${c}`}
                onClick={() => setCat(cat === c ? null : c)}
                className={`chip chip-img ${cat === c ? "chip-active" : ""}`}
              >
                <img src={CATEGORY_META[c].image} alt="" className="chip-thumb" loading="lazy" />
                {tc(c)}
              </button>
            ))}
          </div>
          <Button type="submit" data-testid="hero-search-btn" className="btn-primary mt-5 h-11 w-full rounded-xl">
            {t("hero_cta")}
          </Button>
        </form>
      </section>

      {/* Category gallery */}
      <section className="mt-16" data-testid="category-gallery">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base text-ppc-text md:text-lg">{t("section_categories")}</h2>
          <Link to="/browse" data-testid="gallery-see-all-link" className="text-[13px] text-primary-green">
            {SEE_ALL[lang] || SEE_ALL.en}
          </Link>
        </div>
        <p className="mt-1 text-sm text-ppc-muted">{t("section_categories_sub")}</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ALL_CATEGORIES.map((c) => (
            <CategoryCard key={c} category={c} count={counts[c] || 0} />
          ))}
        </div>
      </section>

      {/* Intro video (per language) */}
      {INTRO_VIDEO[lang] && (
        <section className="mt-14" data-testid="intro-video-section">
          <h2 className="text-base text-ppc-text md:text-lg">{INTRO_VIDEO[lang].title}</h2>
          <p className="mt-1 text-sm text-ppc-muted">{INTRO_VIDEO[lang].sub}</p>
          <div
            className="mx-auto mt-5 overflow-hidden rounded-3xl border border-ppc bg-black shadow-[0_18px_50px_rgba(0,0,0,0.45)] lg:max-w-3xl"
            style={{ aspectRatio: "16 / 9" }}
          >
            <video
              key={lang}
              data-testid="intro-video"
              src={INTRO_VIDEO[lang].src}
              controls
              playsInline
              preload="metadata"
              className="h-full w-full object-contain"
            />
          </div>
        </section>
      )}

      {/* Nearby */}
      <section className="mt-16">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base text-ppc-text md:text-lg">{t("browse_title")}</h2>
          <Link to="/browse" data-testid="see-all-link" className="text-[13px] text-primary-green">
            {SEE_ALL[lang] || SEE_ALL.en}
          </Link>
        </div>
        <div className="mt-3 grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
          {featured === null
            ? [0, 1, 2].map((i) => <div key={i} className="skeleton h-[92px]" />)
            : featured.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      </section>

      {/* How it works */}
      <section className="mt-20">
        <h2 className="text-base text-ppc-text md:text-lg">{t("how_title")}</h2>
        <p className="mt-1 text-sm text-ppc-muted">{t("how_sub")}</p>
        <div className="mt-5 grid gap-2.5 md:grid-cols-3">
          {[
            { icon: MagnifyingGlass, t: t("how1_t"), d: t("how1_d") },
            { icon: CalendarCheck, t: t("how2_t"), d: t("how2_d") },
            { icon: Sparkle, t: t("how3_t"), d: t("how3_d") },
          ].map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} className="card-list p-6">
                <div className="flex items-center justify-between">
                  <span className="grid h-10 w-10 place-items-center rounded-xl border border-ppc bg-ppc-bg">
                    <Icon size={18} color="#9184d9" />
                  </span>
                  <span className="label-caps">0{i + 1}</span>
                </div>
                <h3 className="mt-5 text-[15px] text-ppc-text">{step.t}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ppc-muted">{step.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Two audiences */}
      <section className="mt-20 grid gap-4 md:grid-cols-2">
        <div className="card-focal flex flex-col justify-between p-8">
          <div>
            <Clock size={28} color="#9184d9" />
            <h3 className="mt-5 text-2xl text-ppc-text">{t("busy_title")}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ppc-muted">{t("busy_d")}</p>
          </div>
          <Button asChild data-testid="busy-cta-btn" className="btn-primary mt-8 w-fit rounded-xl px-6">
            <Link to="/browse">{t("busy_cta")}</Link>
          </Button>
        </div>
        <div className="card-list flex flex-col justify-between p-8">
          <div>
            <Briefcase size={28} color="#9184d9" />
            <h3 className="mt-5 text-2xl text-ppc-text">{t("prov_title")}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ppc-muted">{t("prov_d")}</p>
          </div>
          <Button asChild data-testid="provider-cta-btn" className="btn-secondary mt-8 w-fit rounded-xl px-6">
            <Link to="/create-listing">{t("prov_cta")}</Link>
          </Button>
        </div>
      </section>

      <p className="mt-10 flex items-center justify-center gap-1.5 text-xs text-ppc-muted">
        <Lightning size={14} weight="fill" color="#9184d9" /> {t("app_tagline")}
      </p>
    </div>
  );
}
